import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { templatesApi } from '../api/templates';
import { eventsApi } from '../api/events';
import { getErrorMessage } from '../api/client';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Alert from '../components/ui/Alert';
import Spinner from '../components/ui/Spinner';
import DynamicForm from '../components/DynamicForm';
import TemplateRenderer from '../templates/TemplateRenderer';

export default function CreateEvent() {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [title, setTitle] = useState('');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [createdEvent, setCreatedEvent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const list = await templatesApi.list();
        const t = list.find((x) => x.id === templateId);
        if (!t) { setError('Plantilla no encontrada'); return; }
        setTemplate(t);
        setTitle(`${t.name} — mi evento`);
        const initial = {};
        (t.schema?.fields || []).forEach((f) => { initial[f.key] = ''; });
        setData(initial);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [templateId]);

  const validate = () => {
    const missing = (template.schema?.fields || [])
      .filter((f) => f.required && !data[f.key])
      .map((f) => f.label);
    if (missing.length) {
      setError(`Completa: ${missing.join(', ')}`);
      return false;
    }
    if (!title.trim()) { setError('Ponle un título al evento.'); return false; }
    return true;
  };

  const handleSaveDraft = async () => {
    setError('');
    if (!validate()) return;
    setSaving(true);
    try {
      const ev = await eventsApi.create({
        templateId: template.id, title: title.trim(), data,
      });
      setCreatedEvent(ev);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setError('');
    setPublishing(true);
    try {
      let ev = createdEvent;
      if (!ev) {
        if (!validate()) { setPublishing(false); return; }
        ev = await eventsApi.create({
          templateId: template.id, title: title.trim(), data,
        });
      }
      await eventsApi.publish(ev.id);
      navigate(`/events/${ev.id}`);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return <div className="grid place-items-center py-20"><Spinner size="lg" className="text-brand-500" /></div>;
  }

  if (!template) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500 mb-4">{error || 'Plantilla no encontrada'}</p>
        <Link to="/templates" className="font-semibold text-brand-600 hover:underline">
          Volver a plantillas
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/templates" className="text-xs text-slate-500 hover:text-brand-600">
            ← Volver a plantillas
          </Link>
          <h1 className="text-2xl font-extrabold mt-2">Personaliza tu evento</h1>
          <p className="text-sm text-slate-500">
            Plantilla: <b>{template.name}</b> — {template.category}
          </p>
        </div>
        <Button variant="secondary" onClick={() => setShowPreview((v) => !v)}>
          {showPreview ? 'Ocultar vista previa' : 'Ver cómo queda'}
        </Button>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      {showPreview ? (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="bg-slate-900 text-white text-xs px-4 py-2 text-center">
            Vista previa con los datos actuales
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            <TemplateRenderer
              template={template}
              data={data}
              title={title}
            />
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
            <Input
              label="Título del evento (solo tú lo verás)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: Boda Ana & Luis"
              required
            />
            <div className="border-t border-slate-100 pt-4">
              <h2 className="text-sm font-bold text-slate-700 mb-4">Datos de la invitación</h2>
              <DynamicForm schema={template.schema} values={data} onChange={setData} />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100">
              <Button variant="secondary" onClick={handleSaveDraft} disabled={saving || publishing} className="flex-1">
                {saving ? 'Guardando...' : 'Guardar borrador'}
              </Button>
              <Button onClick={handlePublish} disabled={saving || publishing} className="flex-1">
                {publishing ? 'Publicando...' : 'Publicar ahora'}
              </Button>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-5">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">
                Tips
              </p>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex gap-2">
                  <span className="text-brand-500">→</span>
                  Puedes guardar el borrador y editarlo después.
                </li>
                <li className="flex gap-2">
                  <span className="text-brand-500">→</span>
                  Al publicar se activa 3 días gratis.
                </li>
                <li className="flex gap-2">
                  <span className="text-brand-500">→</span>
                  Comparte el link por WhatsApp con un clic.
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}